///
/// EarthBrowser
///
/// Migration v9 to v10: Rename locationFocusPanel to infoWindow
///
/// This migration converts the locationFocusPanel field to the new infoWindow
/// format which supports multiple content types (location, satellite, volcano, etc.)
///

import type { LocationInfo } from "@shared-types/location-info"

interface OldLocationFocusPanelState {
	isOpen: boolean
	position: { x: number; y: number }
	locationInfo: LocationInfo | null
	countryName: string | null
}

interface UserInterfaceV9 {
	locationFocusPanel?: OldLocationFocusPanelState
}

interface StateV9 {
	userInterface?: UserInterfaceV9
}

interface NewInfoWindowState {
	isOpen: boolean
	position: { x: number; y: number }
	size: { width: number; height: number }
	title: string
	subtitle?: string
	contentType: string | null
	contentData: unknown
}

/**
 * Migration from version 9 to 10: Rename locationFocusPanel to infoWindow
 * - Converts locationFocusPanel to the new infoWindow format
 * - Adds size, title, contentType, and contentData fields
 */
export function migrateV9toV10(state: unknown): unknown {
	if (!state) {
		return state
	}

	const s = state as StateV9
	const oldUserInterface = s.userInterface || {}
	const oldPanel: OldLocationFocusPanelState | undefined = oldUserInterface.locationFocusPanel

	// Create the new infoWindow state
	let newInfoWindow: NewInfoWindowState = {
		isOpen: false,
		position: { x: 100, y: 100 },
		size: { width: 320, height: 280 },
		title: "",
		subtitle: undefined,
		contentType: null,
		contentData: null
	}

	// Migrate data from old locationFocusPanel if it exists
	if (oldPanel) {
		const locationInfo = oldPanel.locationInfo

		// Build title from location info
		let title = ""
		if (locationInfo) {
			title = locationInfo.name
			if (locationInfo.country === "US" && locationInfo.admin1) {
				title = `${locationInfo.name}, ${locationInfo.admin1}`
			}
		}

		newInfoWindow = {
			isOpen: oldPanel.isOpen ?? false,
			position: oldPanel.position ?? { x: 100, y: 100 },
			size: { width: 320, height: 280 },
			title,
			subtitle: undefined,
			contentType: locationInfo ? "location" : null,
			contentData: locationInfo ? {
				type: "location",
				locationInfo,
				countryName: oldPanel.countryName
			} : null
		}
	}

	// Create new userInterface state with infoWindow instead of locationFocusPanel
	const restUserInterface = { ...oldUserInterface }
	delete restUserInterface.locationFocusPanel
	const newUserInterface = {
		...restUserInterface,
		infoWindow: newInfoWindow
	}

	// Return completely new state object
	return {
		...s,
		userInterface: newUserInterface
	}
}
